import { Fragment, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
    FiChevronDown,
    FiChevronRight,
    FiZap,
    FiSave,
    FiCheckCircle,
    FiAlertTriangle,
    FiUsers,
} from "react-icons/fi";
import promotionService from "../../services/promotionService";
import courseService from "../../services/courseService";
import { academicYearService } from "../../services/yearService";
import { getYearOptions } from "../../utils/courseYears";

const STATUS_OPTIONS = ["Active", "Passed", "ATKT", "Detained", "Dropped", "Passed Out"];
const STATUS_BADGE = {
    Active: { bg: "#e2e8f0", fg: "#475569" },
    Passed: { bg: "#dcfce7", fg: "#166534" },
    ATKT: { bg: "#fef3c7", fg: "#b45309" },
    Detained: { bg: "#fee2e2", fg: "#b91c1c" },
    Dropped: { bg: "#f1f5f9", fg: "#64748b" },
    "Passed Out": { bg: "#e8f1f8", fg: "#002747" },
};

function StatusBadge({ status }) {
    const c = STATUS_BADGE[status] || STATUS_BADGE.Active;
    return (
        <span className="badge" style={{ background: c.bg, color: c.fg }}>
            {status}
        </span>
    );
}

export default function PromoteStudents() {
    const [academicYears, setAcademicYears] = useState([]);
    const [courses, setCourses] = useState([]);
    const [academicYearId, setAcademicYearId] = useState("");
    const [courseId, setCourseId] = useState("");
    const [yearNumber, setYearNumber] = useState("");

    const [loadingClass, setLoadingClass] = useState(false);
    const [classData, setClassData] = useState(null); // { course, universityExam, roster }
    const [rows, setRows] = useState({}); // studentId -> { status, backlog: [], expanded }
    const [saving, setSaving] = useState(false);

    const selectedCourse = courses.find((c) => c._id === courseId);
    const yearOptions = selectedCourse ? getYearOptions(selectedCourse) : [];

    useEffect(() => {
        (async () => {
            try {
                const [ayRes, courseRes] = await Promise.all([
                    academicYearService.getAll(),
                    courseService.getAll({ isActive: true }),
                ]);
                setAcademicYears(ayRes.data || []);
                setCourses(courseRes.data || []);
            } catch (err) {
                toast.error("Couldn't load setup data: " + err.message);
            }
        })();
    }, []);

    const initializeRows = (roster) => {
        const next = {};
        roster.forEach((entry) => {
            next[entry.student._id] = {
                status: entry.student.status,
                backlog: (entry.existingBacklogSubjects || []).map((b) => ({ ...b })),
                expanded: false,
            };
        });
        setRows(next);
    };

    const loadClass = async () => {
        if (!academicYearId || !courseId || !yearNumber) {
            toast.error("Select academic year, course, and year first.");
            return;
        }
        setLoadingClass(true);
        setClassData(null);
        try {
            const res = await promotionService.getClass({
                academicYear: academicYearId,
                course: courseId,
                yearNumber,
            });
            setClassData(res.data);
            initializeRows(res.data.roster || []);
            if (!res.data.universityExam) {
                toast("No University exam found for this class — set statuses manually.", { icon: "⚠️" });
            }
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setLoadingClass(false);
        }
    };

    const applySuggestion = (entry) => {
        const studentId = entry.student._id;
        if (!entry.suggestedStatus) {
            toast.error(`No result found for ${entry.student.fullName} — set status manually.`);
            return;
        }
        setRows((prev) => {
            const current = prev[studentId];
            let backlog = [...current.backlog];

            // Mark suggested clearances
            entry.suggestedClearances.forEach((name) => {
                backlog = backlog.map((b) =>
                    b.subjectName === name && b.status === "Pending"
                        ? {
                            ...b,
                            status: "Cleared",
                            clearedAt: new Date().toISOString(),
                            clearedInExam: classData?.universityExam?._id,
                        }
                        : b
                );
            });

            // Add newly-failed subjects as backlog when suggestion is ATKT
            if (entry.suggestedStatus === "ATKT") {
                entry.suggestedNewBacklogSubjects.forEach((name) => {
                    if (!backlog.some((b) => b.subjectName === name && b.status === "Pending")) {
                        backlog.push({
                            subjectName: name,
                            status: "Pending",
                            fromExam: classData?.universityExam?._id,
                            fromAcademicYear: academicYearId,
                        });
                    }
                });
            }

            return {
                ...prev,
                [studentId]: { ...current, status: entry.suggestedStatus, backlog, expanded: true },
            };
        });
    };

    const applyAllSuggestions = () => {
        if (!classData) return;
        const applicable = classData.roster.filter((e) => e.suggestedStatus);
        if (applicable.length === 0) {
            toast.error("No results found to suggest from for this class.");
            return;
        }
        applicable.forEach(applySuggestion);
        toast.success(`Applied suggestions for ${applicable.length} student(s).`);
    };

    const updateStatus = (studentId, status) => {
        setRows((prev) => ({ ...prev, [studentId]: { ...prev[studentId], status } }));
    };

    const toggleExpanded = (studentId) => {
        setRows((prev) => ({
            ...prev,
            [studentId]: { ...prev[studentId], expanded: !prev[studentId].expanded },
        }));
    };

    const toggleFailedSubjectAsBacklog = (studentId, subjectName, checked) => {
        setRows((prev) => {
            const current = prev[studentId];
            let backlog = [...current.backlog];
            if (checked) {
                if (!backlog.some((b) => b.subjectName === subjectName && b.status === "Pending")) {
                    backlog.push({
                        subjectName,
                        status: "Pending",
                        fromExam: classData?.universityExam?._id,
                        fromAcademicYear: academicYearId,
                    });
                }
            } else {
                backlog = backlog.filter((b) => !(b.subjectName === subjectName && b.status === "Pending"));
            }
            return { ...prev, [studentId]: { ...current, backlog } };
        });
    };

    const toggleBacklogEntryStatus = (studentId, index) => {
        setRows((prev) => {
            const current = prev[studentId];
            const backlog = current.backlog.map((b, i) => {
                if (i !== index) return b;
                const nowClearing = b.status === "Pending";
                return {
                    ...b,
                    status: nowClearing ? "Cleared" : "Pending",
                    clearedAt: nowClearing ? new Date().toISOString() : null,
                    clearedInExam: nowClearing ? classData?.universityExam?._id : null,
                };
            });
            return { ...prev, [studentId]: { ...current, backlog } };
        });
    };

    const pendingCount = (studentId) =>
        (rows[studentId]?.backlog || []).filter((b) => b.status === "Pending").length;

    const handleSaveAll = async () => {
        if (!classData) return;
        const updates = classData.roster.map((entry) => {
            const row = rows[entry.student._id];
            return {
                studentId: entry.student._id,
                status: row.status,
                backlogSubjects: row.backlog,
            };
        });

        setSaving(true);
        try {
            const res = await promotionService.bulkSave(updates);
            toast.success(res.data.message || "Saved.");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setSaving(false);
        }
    };

    const summary = useMemo(() => {
        if (!classData) return null;
        const counts = {};
        Object.values(rows).forEach((r) => {
            counts[r.status] = (counts[r.status] || 0) + 1;
        });
        return counts;
    }, [rows, classData]);

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Promote Students</h1>
                    <p className="page-subtitle">
                        Review a class, set outcomes (Passed / ATKT / Detained / Dropped), and manage backlog subjects — before
                        running Carry Forward in Settings.
                    </p>
                </div>
            </div>

            <div className="card" style={{ marginBottom: "1.25rem" }}>
                <div className="card-body">
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">Academic Year</label>
                            <select
                                className="form-control"
                                value={academicYearId}
                                onChange={(e) => setAcademicYearId(e.target.value)}
                            >
                                <option value="">Select Academic Year</option>
                                {academicYears.map((y) => (
                                    <option key={y._id} value={y._id}>
                                        {y.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Course</label>
                            <select
                                className="form-control"
                                value={courseId}
                                onChange={(e) => {
                                    setCourseId(e.target.value);
                                    setYearNumber("");
                                }}
                            >
                                <option value="">Select Course</option>
                                {courses.map((c) => (
                                    <option key={c._id} value={c._id}>
                                        {c.shortName}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">Year</label>
                            <select
                                className="form-control"
                                value={yearNumber}
                                onChange={(e) => setYearNumber(e.target.value)}
                                disabled={!courseId}
                            >
                                <option value="">{courseId ? "Select Year" : "Select a course first"}</option>
                                {yearOptions.map((y) => (
                                    <option key={y.yearNumber} value={y.yearNumber}>
                                        {y.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group" style={{ alignSelf: "end" }}>
                            <button className="btn btn-primary" onClick={loadClass} disabled={loadingClass}>
                                <FiUsers size={14} style={{ marginRight: 6 }} />
                                {loadingClass ? "Loading..." : "Load Class"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {classData && (
                <>
                    <div
                        className="card"
                        style={{
                            marginBottom: "1.25rem",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "0.75rem",
                            padding: "0.9rem 1.1rem",
                        }}
                    >
                        <div style={{ fontSize: 13 }}>
                            <strong>{classData.course.shortName}</strong> · ATKT limit:{" "}
                            <strong>{classData.course.atktLimit}</strong> subject(s) ·{" "}
                            {classData.universityExam ? (
                                <span style={{ color: "#166534" }}>
                                    <FiCheckCircle size={12} style={{ verticalAlign: -1, marginRight: 3 }} />
                                    University exam found: {classData.universityExam.title}
                                </span>
                            ) : (
                                <span style={{ color: "#b91c1c" }}>
                                    <FiAlertTriangle size={12} style={{ verticalAlign: -1, marginRight: 3 }} />
                                    No University exam found — set statuses manually
                                </span>
                            )}
                            {summary && (
                                <div style={{ marginTop: 6, color: "var(--text-muted)" }}>
                                    {Object.entries(summary)
                                        .map(([status, count]) => `${status}: ${count}`)
                                        .join(" · ")}
                                </div>
                            )}
                        </div>
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button className="btn btn-outline" onClick={applyAllSuggestions} disabled={!classData.universityExam}>
                                <FiZap size={14} style={{ marginRight: 6 }} />
                                Suggest All from Results
                            </button>
                            <button className="btn btn-primary" onClick={handleSaveAll} disabled={saving}>
                                <FiSave size={14} style={{ marginRight: 6 }} />
                                {saving ? "Saving..." : "Save All"}
                            </button>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body" style={{ padding: 0 }}>
                            {classData.roster.length === 0 ? (
                                <div className="empty-state">
                                    <FiUsers />
                                    <p>No students found in this class.</p>
                                </div>
                            ) : (
                                <div className="table-wrapper">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Student</th>
                                                <th>Current Status</th>
                                                <th>University Result</th>
                                                <th>New Status</th>
                                                <th>Backlog</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {classData.roster.map((entry) => {
                                                const studentId = entry.student._id;
                                                const row = rows[studentId];
                                                if (!row) return null;
                                                return (
                                                    <Fragment key={studentId}>
                                                        <tr key={studentId}>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleExpanded(studentId)}
                                                                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                                                                >
                                                                    {row.expanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                                                                </button>
                                                            </td>
                                                            <td>
                                                                <div style={{ fontWeight: 600 }}>{entry.student.fullName}</div>
                                                                <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                                    {entry.student.enrollmentNo}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <StatusBadge status={entry.student.status} />
                                                            </td>
                                                            <td style={{ fontSize: 12.5 }}>
                                                                {entry.universityResult ? (
                                                                    <>
                                                                        {entry.universityResult.result} · {entry.universityResult.percentage}% ·{" "}
                                                                        {entry.universityResult.grade}
                                                                    </>
                                                                ) : (
                                                                    <span style={{ color: "var(--text-muted)" }}>No result</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <select
                                                                    className="form-control"
                                                                    style={{ fontSize: 12.5, padding: "0.3rem 0.5rem" }}
                                                                    value={row.status}
                                                                    onChange={(e) => updateStatus(studentId, e.target.value)}
                                                                >
                                                                    {STATUS_OPTIONS.map((s) => (
                                                                        <option key={s} value={s}>
                                                                            {s}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                            <td>
                                                                {pendingCount(studentId) > 0 ? (
                                                                    <span className="badge" style={{ background: "#fef3c7", color: "#b45309" }}>
                                                                        {pendingCount(studentId)} pending
                                                                    </span>
                                                                ) : (
                                                                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>—</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-outline btn-sm"
                                                                    onClick={() => applySuggestion(entry)}
                                                                    disabled={!entry.suggestedStatus}
                                                                    title={entry.suggestedStatus ? `Suggest: ${entry.suggestedStatus}` : "No suggestion available"}
                                                                >
                                                                    <FiZap size={12} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        {row.expanded && (
                                                            <tr key={`${studentId}-detail`}>
                                                                <td></td>
                                                                <td colSpan={6} style={{ background: "#f8fafc", padding: "0.85rem 1rem" }}>
                                                                    <div style={{ display: "grid", gap: "0.85rem" }}>
                                                                        {entry.failedSubjects.length > 0 && (
                                                                            <div>
                                                                                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>
                                                                                    FAILED SUBJECTS (this exam)
                                                                                </div>
                                                                                <div style={{ display: "grid", gap: "0.3rem" }}>
                                                                                    {entry.failedSubjects.map((f) => {
                                                                                        const included = row.backlog.some(
                                                                                            (b) => b.subjectName === f.subjectName && b.status === "Pending"
                                                                                        );
                                                                                        return (
                                                                                            <label
                                                                                                key={f.subjectName}
                                                                                                style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: 12.5 }}
                                                                                            >
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    checked={included}
                                                                                                    onChange={(e) =>
                                                                                                        toggleFailedSubjectAsBacklog(studentId, f.subjectName, e.target.checked)
                                                                                                    }
                                                                                                />
                                                                                                {f.subjectName} — {f.isAbsent ? "Absent" : `${f.marksObtained}/${f.passingMarks} needed`}
                                                                                                <span style={{ color: "var(--text-muted)" }}>(track as backlog)</span>
                                                                                            </label>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        )}

                                                                        <div>
                                                                            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>
                                                                                BACKLOG SUBJECTS ({row.backlog.length})
                                                                            </div>
                                                                            {row.backlog.length === 0 ? (
                                                                                <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: 0 }}>None.</p>
                                                                            ) : (
                                                                                <div style={{ display: "grid", gap: "0.3rem" }}>
                                                                                    {row.backlog.map((b, i) => (
                                                                                        <div
                                                                                            key={`${b.subjectName}-${i}`}
                                                                                            style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: 12.5 }}
                                                                                        >
                                                                                            <span
                                                                                                className="badge"
                                                                                                style={
                                                                                                    b.status === "Cleared"
                                                                                                        ? { background: "#dcfce7", color: "#166534" }
                                                                                                        : { background: "#fef3c7", color: "#b45309" }
                                                                                                }
                                                                                            >
                                                                                                {b.status}
                                                                                            </span>
                                                                                            {b.subjectName}
                                                                                            <button
                                                                                                type="button"
                                                                                                className="btn btn-outline btn-sm"
                                                                                                onClick={() => toggleBacklogEntryStatus(studentId, i)}
                                                                                            >
                                                                                                Mark {b.status === "Pending" ? "Cleared" : "Pending"}
                                                                                            </button>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}